import { useState, useCallback, useEffect, useMemo } from "react";
import { ColumnsType, ColumnVisibility } from "@/types/tableState.types";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import isEqual from "lodash/isEqual";
import ColumnsMenu from "./ColumnsMenu";

const mergeWithApiColumns = (
  columns: ColumnsType[],
  apiColumns?: string[],
): ColumnsType[] => {
  // Track fieldNames as we go so neither the incoming columns nor the API list
  // can introduce a duplicate. A duplicate fieldName renders twice in the
  // selector and shares one visibility toggle, so the field can't be hidden
  // individually.
  const seen = new Set<string>();
  const result: ColumnsType[] = [];

  for (const column of columns) {
    if (seen.has(column.fieldName)) continue;
    seen.add(column.fieldName);
    result.push(column);
  }

  for (const col of apiColumns ?? []) {
    if (seen.has(col)) continue;
    seen.add(col);
    result.push({
      fieldName: col,
      visibility: ColumnVisibility.HIDDEN,
      mandatory: false,
    });
  }

  return result;
};

type ColumnChangeProperties = {
  visibility?: ColumnVisibility;
  mandatory?: boolean;
};

type ColumnButtonProps = {
  columns: ColumnsType[];
  setColumns: (columns: ColumnsType[]) => void;
  lockedColumns: number;
  setLockedColumns: (count: number) => void;
  columnsTitleCreator?: (column: string) => string;
  initialColumns?: ColumnsType[];
  initialLockedColumns?: number;
  apiColumns?: string[];
};

export const ColumnButton = ({
  columns,
  setColumns,
  lockedColumns,
  setLockedColumns,
  columnsTitleCreator,
  initialColumns,
  initialLockedColumns,
  apiColumns,
}: ColumnButtonProps) => {
  const { isSuperuserMode } = useSuperuserMode();
  const [isOpen, setIsOpen] = useState(false);

  // Merge columns with API columns to include any new columns
  const mergedColumns = useMemo(
    () => mergeWithApiColumns(columns, apiColumns),
    [columns, apiColumns],
  );

  const [editedColumns, setEditedColumns] =
    useState<ColumnsType[]>(mergedColumns);
  const [selectedColumnCount, setSelectedColumnCount] = useState(lockedColumns);

  useEffect(() => {
    setEditedColumns(mergedColumns);
  }, [mergedColumns]);

  useEffect(() => {
    setSelectedColumnCount(lockedColumns);
  }, [lockedColumns]);

  const hasChanged = initialColumns
    ? !isEqual(columns, initialColumns) ||
      lockedColumns !== initialLockedColumns
    : false;

  const changeOneColumn = useCallback(
    (column: ColumnsType, newProperties: ColumnChangeProperties) => {
      setEditedColumns((editedColumns) =>
        editedColumns.map((c) =>
          c.fieldName === column.fieldName ? { ...c, ...newProperties } : c,
        ),
      );
    },
    [],
  );

  const onApply = () => {
    setLockedColumns(selectedColumnCount);
    setColumns(editedColumns);
  };

  const onReset = () => {
    setEditedColumns(mergedColumns);
    setSelectedColumnCount(lockedColumns);
  };

  return (
    <ColumnsMenu
      open={isOpen}
      setOpen={setIsOpen}
      editedColumns={editedColumns}
      setEditedColumns={setEditedColumns}
      columnsTitleCreator={columnsTitleCreator}
      isSuperuserMode={isSuperuserMode}
      selectedColumnCount={selectedColumnCount}
      setSelectedColumnCount={setSelectedColumnCount}
      changeOneColumn={changeOneColumn}
      onApply={onApply}
      onReset={onReset}
      hasChanged={hasChanged}
      menuHeight="450px"
      menuWidth="620px"
    />
  );
};
